import { FC, ReactNode, useState } from 'react';
import { Control, useFieldArray } from 'react-hook-form';
import { InitialValues } from '../schema';
import { useLabelWithTooltip } from '../../../../../hooks';
import { Box, Typography } from '@mui/material';
import { maxFileSizeInBytes } from '../../../../../constants';
import SunstoneDocumentFileDropzone from '../../../../SunstoneDocumentFileDropzone';
import {
  RequiredDocument,
  SunstoneDocument,
} from '../../../../../../../../types/api';
import FileInput from '../FileInput';
import { DocumentIcon } from '../../../../loan_application/icons';
import bytesToSize from '../../../../../utils/bytesToSize';
import ShowIf from '../../../../ConditionalRender/ShowIf';

type DocumentUploadFieldsetProps = {
  label: ReactNode;
  name: keyof Pick<InitialValues, 'utility_bills' | 'incentive_documents'>;
  max: number;
  required?: boolean;
  tooltip?: string;
  uploadMoreText?: ReactNode;
  additionalInfo?: string;
  control: Control<InitialValues>;
  onFileChange?: any;
  onFileRemove?: (documentExternalId: string) => void;
  onUploadResponse?: (
    document: RequiredDocument,
    name: any
  ) => Promise<any | undefined>;
};

export const DocumentUploadFieldset: FC<DocumentUploadFieldsetProps> = ({
  label,
  name,
  max,
  tooltip,
  required,
  uploadMoreText,
  additionalInfo,
  control,
  onFileChange,
  onFileRemove,
  onUploadResponse,
}) => {
  const arrayHelpers = useFieldArray({
    control,
    name: name,
  });

  const { fields } = arrayHelpers;
  const [fileError, setFileError] = useState<string | null>(null);

  const [lastAnalysis, setLastAnalysis] = useState<boolean | null | undefined>(
    undefined
  );
  const [lastFileName, setLastFileName] = useState<string>('');

  const labelWithTooltip = useLabelWithTooltip(label, tooltip);

  const handleError = (error: Error | null) => {
    setFileError(error?.message || null);
  };

  return (
    <>
      <Box component={'fieldset'} border={'none'} p={0} m={0}>
        {fields.map((field, index) => (
          <SunstoneDocumentFileDropzone
            tooltip={tooltip}
            required={required}
            label={index === 0 ? label : null}
            remove={() => {
              const file: File & any = field.file;
              if (file.external_id && onFileRemove)
                onFileRemove(file.external_id);
              arrayHelpers.remove(index);
            }}
            onDrop={async (acceptedFiles, rejections) => {
              arrayHelpers.update(index, {
                file: acceptedFiles[0],
              });
              if (onFileChange) await onFileChange(acceptedFiles[0], name);
              handleError(null);

              // Trigger analysis after upload
              try {
                const externalId = (acceptedFiles[0] as any)?.external_id;
                if (onUploadResponse && externalId) {
                  setLastFileName(acceptedFiles[0].name ?? '');
                  setLastAnalysis(null);
                  const minimalDoc = {
                    files: [{ id: externalId }],
                  } as unknown as RequiredDocument;
                  const result = await onUploadResponse(minimalDoc, name);
                  const recognized =
                    result?.result?.output?.validations?.filter(
                      (v: any) => v.id == 'is_document_recognized'
                    )?.[0]?.result ?? true;
                  setLastAnalysis(Boolean(recognized));
                }
              } catch (e: any) {
                setLastAnalysis(undefined);
                console.error('Error triggering analysis:', e.message);
              }
            }}
            onError={handleError}
            canReplace
            accept={{
              'image/*': [],
              'application/pdf': [],
              'application/doc': ['.doc', '.docx'],
              'application/text': ['.txt'],
            }}
            maxSize={maxFileSizeInBytes}
            file={
              {
                name: field.file?.name || '',
                display_name: field.file?.name || '',
              } as SunstoneDocument
            }
            hideMaxSize
            key={field.id}
            additionalInfo={additionalInfo}
          />
        ))}
        {fields.length < max && (
          <FileInput
            required={fields.length ? undefined : required}
            data-testid={`${name}-input`}
            key={fields.length + 'new'}
            label={fields.length ? null : labelWithTooltip}
            buttonContent={fields.length ? uploadMoreText : undefined}
            startAdornment={<DocumentIcon />}
            maxSize={maxFileSizeInBytes}
            error={!!fileError}
            errorMessage={fileError}
            onFileRemove={onFileChange ?? (() => {})}
            onFileChange={async (acceptedFiles, rejections) => {
              if (acceptedFiles.length) {
                arrayHelpers.append({
                  file: acceptedFiles[0],
                });
                setFileError(null);
                if (onFileChange) await onFileChange(acceptedFiles[0], name);

                // Trigger analysis after upload
                try {
                  const externalId = (acceptedFiles[0] as any)?.external_id;
                  if (onUploadResponse && externalId) {
                    setLastFileName(acceptedFiles[0].name ?? '');
                    setLastAnalysis(null);
                    const minimalDoc = {
                      files: [{ id: externalId }],
                    } as unknown as RequiredDocument;
                    const result = await onUploadResponse(minimalDoc, name);
                    const recognized =
                      result?.result?.output?.validations?.filter(
                        (v: any) => v.id == 'is_document_recognized'
                      )?.[0]?.result ?? true;
                    setLastAnalysis(Boolean(recognized));
                  }
                } catch (e: any) {
                  setLastAnalysis(undefined);
                  console.error('Error triggering analysis:', e.message);
                }
              } else if (rejections.length) {
                const file = rejections[0].file;
                const error = rejections[0].errors[0];
                let errorCopy: string;
                if (error.code === 'file-too-large') {
                  errorCopy = `${
                    file.name
                  } is too big. Max size is ${bytesToSize(
                    maxFileSizeInBytes
                  )}.`;
                } else if (error.code === 'file-invalid-type') {
                  errorCopy = `${file.name} is not a valid file type. Should be an image, PDF, or a text document.`;
                } else {
                  errorCopy = error.message;
                }
                setFileError(errorCopy);
              }
            }}
            accept={{
              'image/*': [],
              'application/pdf': [],
              'application/doc': ['.doc', '.docx'],
              'application/text': ['.txt'],
            }}
            additionalInfo={
              additionalInfo && fields.length === 0 ? additionalInfo : ''
            }
          />
        )}
      </Box>
      <ShowIf condition={lastAnalysis === null}>
        <div style={{ color: '#868795', marginTop: '-20px' }}>
          Analyzing file {(lastFileName ?? '').slice(0, 60)}...
        </div>
      </ShowIf>

      <ShowIf condition={lastAnalysis === false}>
        <Box
          sx={{
            paddingLeft: '1rem',
            borderLeft: '4px solid #FFCC00',
            marginTop: '-1rem',
            marginBottom: '1rem',
          }}
        >
          <Typography variant="caption" fontSize="13px">
            This may not be a{' '}
            {name === 'utility_bills' ? 'Utility Bill' : 'Incentive Document'}.
            Incorrect files can delay the review process. <br />
            Proceed if you are sure this file is correct.
          </Typography>
        </Box>
      </ShowIf>
    </>
  );
};
